import type { Component, VoidComponent } from "solid-js";
import { createMemo, For, Match, Show, Switch } from "solid-js";
import { type RouteSectionProps, useNavigate } from "@solidjs/router";
import { useTranslation } from "../../../../state/i18n";
import TextLink from "../../../../components/link/TextLink";
import Pill from "../../../../components/pill/Pill";
import FormatCurrency from "../../../../components/formatting/FormatCurrency";
import { TextAndIconButton, TextButton } from "../../../../components/button/Button";
import FormatDate from "../../../../components/formatting/FormatDate";
import withPagePermissionCheck from "../../../../utils/withPagePermissionCheck";
import PermissionService from "../../../../services/PermissionService";
import BillingService from "../../../../services/BillingService";
import { Icon } from "../../../../components/icon/Icon";
import type { Invoice, UpcomingInvoice } from "../../../../types/Invoice";
import { isEmpty, isNil } from "lodash-es";
import SettingsPageHeader from "../components/SettingsPageHeader.tsx";
import SeparatorLine from "../../../../components/SeparatorLine.tsx";
import DetailModal from "../../../../components/modal/DetailModal.tsx";
import { useModalManager } from "../../../../components/modal/ModalManager.tsx";
import { condition } from "../../../../utils/solidUtils.ts";
import { QueryBoundary } from "../../../../utils/QueryBoundary.tsx";

const InvoicesPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const invoicesQuery = BillingService.getInvoices();
  const upcomingInvoiceQuery = BillingService.getUpcomingInvoice();

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_BILLING_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_INVOICES_LABEL)}
      />

      <QueryBoundary>
        <div
          class="-mx-0.5 flex h-full min-h-0 flex-col gap-10 overflow-y-auto px-0.5 pt-8 pb-6"
          aria-busy={invoicesQuery.isFetching || upcomingInvoiceQuery.isFetching}
        >
          <Show
            when={condition(
              { invoicesQuery, upcomingInvoiceQuery },
              ({ invoicesQuery, upcomingInvoiceQuery }) => invoicesQuery.isFetched && upcomingInvoiceQuery.isFetched,
            )}
            fallback={
              <div class="flex size-full flex-col items-center justify-center">
                <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
                <span class="text-medium-regular text-primary">{t(($) => $.SETTINGS_INVOICES_LOADING_TITLE)}</span>
              </div>
            }
          >
            {(queries) => {
              const filteredInvoices = createMemo(() =>
                queries().invoicesQuery.data?.filter((invoice) => invoice.invoiceStatus !== "DRAFT"),
              );
              const hasUpcomingInvoice = createMemo(
                () => !(queries().upcomingInvoiceQuery.isError && queries().upcomingInvoiceQuery.error?.status === 400),
              );

              return (
                <>
                  <Show when={hasUpcomingInvoice() && queries().upcomingInvoiceQuery.data}>
                    {(upcomingInvoice) => <UpcomingInvoiceSection upcomingInvoice={upcomingInvoice()} />}
                  </Show>

                  <Show when={filteredInvoices()}>
                    {(data) => (
                      <div class="flex h-full flex-col gap-6">
                        <h4 class="text-header-lg text-primary">{t(($) => $.SETTINGS_INVOICES_PAST_INVOICES_TITLE)}</h4>

                        <div role="table" style={{ "--cols": 5 }} class="flex size-full border-collapse flex-col">
                          <div role="rowgroup" class="table-header-group">
                            <div
                              role="row"
                              class="grid grid-cols-auto-min-table gap-3 border-b border-subtle text-base-semibold text-secondary"
                            >
                              <div role="columnheader" class="py-1.5">
                                {t(($) => $.SETTINGS_INVOICES_TABLE_HEADER_INVOICE_NUMBER)}
                              </div>
                              <div role="columnheader" class="py-1.5">
                                {t(($) => $.SETTINGS_INVOICES_TABLE_HEADER_DATE)}
                              </div>
                              <div role="columnheader" class="py-1.5">
                                {t(($) => $.SETTINGS_INVOICES_TABLE_HEADER_AMOUNT)}
                              </div>
                              <div role="columnheader" class="py-1.5">
                                {t(($) => $.SETTINGS_INVOICES_TABLE_HEADER_STATUS)}
                              </div>
                              <div role="columnheader" class="py-1.5">
                                {t(($) => $.SETTINGS_INVOICES_TABLE_HEADER_PDF)}
                              </div>
                            </div>
                          </div>
                          <Show
                            when={!isEmpty(data())}
                            fallback={
                              <div class="py-4 text-medium-regular text-secondary">
                                {t(($) => $.SETTINGS_INVOICES_TABLE_EMPTY_STATE_TITLE)}
                              </div>
                            }
                          >
                            <div role="rowgroup" class="h-full divide-y divide-subtle">
                              <For each={data()}>
                                {(invoice) => (
                                  <div
                                    role="row"
                                    class="grid grid-cols-auto-min-table items-center gap-3 text-base-regular text-secondary"
                                  >
                                    <div role="cell" class="min-w-30 py-4">
                                      <TextLink label={invoice.invoiceNumber} href={invoice.statusUrl} isExternal />
                                    </div>
                                    <div role="cell" class="min-w-30 py-4">
                                      <FormatDate dateOnly value={invoice.invoiceDate} />
                                    </div>
                                    <div role="cell" class="min-w-30 py-4">
                                      <FormatCurrency
                                        value={invoice.paymentStatus.totalInCents / 100}
                                        currency={invoice.paymentStatus.currency}
                                      />
                                    </div>
                                    <div role="cell" class="min-w-30 py-4">
                                      <InvoiceStatus status={invoice.invoiceStatus} />
                                    </div>
                                    <div role="cell" class="min-w-30 py-4">
                                      <TextAndIconButton
                                        variant="secondary"
                                        size="medium"
                                        text={t(($) => $.DOWNLOAD_BUTTON_LABEL)}
                                        icon="download"
                                        onClick={() => {
                                          window.open(invoice.pdfUrl, "_blank");
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </For>
                            </div>
                          </Show>
                        </div>
                      </div>
                    )}
                  </Show>
                </>
              );
            }}
          </Show>
        </div>
      </QueryBoundary>
    </div>
  );
};

const UpcomingInvoiceSection: VoidComponent<{ upcomingInvoice: UpcomingInvoice }> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const navigate = useNavigate();

  return (
    <div class="flex max-w-400 flex-col gap-8 rounded-lg border border-subtle bg-surface-extra-subtle p-6">
      <div class="flex flex-col gap-8">
        <h4 class="text-header-lg text-primary">{t(($) => $.SETTINGS_INVOICES_UPCOMING_TITLE)}</h4>
      </div>
      <div class="flex items-center">
        <div class="flex flex-col gap-2">
          <p class="text-header-xl text-primary">
            <FormatCurrency value={props.upcomingInvoice.amountDue / 100} currency={props.upcomingInvoice.currency} />
          </p>
          <p class="text-base-medium text-secondary">
            {t(($) => $.SETTINGS_INVOICES_UPCOMING_DUE_ON, {
              dueDate: props.upcomingInvoice.invoiceDate,
              formatParams: { dueDate: { dateStyle: "long" } },
            })}
          </p>
        </div>
        <div class="ml-auto flex gap-1">
          <TextButton
            variant="secondary"
            size="large"
            text={t(($) => $.SETTINGS_INVOICES_UPCOMING_VIEW_SUMMARY_ACTION_LABEL)}
            onClick={() => {
              open({
                content: () => <UpcomingInvoiceSummaryModal upcomingInvoice={props.upcomingInvoice} />,
              });
            }}
          />
          <TextButton
            variant="secondary"
            size="large"
            text={t(($) => $.SETTINGS_INVOICES_UPCOMING_VIEW_USAGE_ACTION_LABEL)}
            onClick={() => {
              navigate("../usage?timeframe=billingPeriod");
            }}
          />
        </div>
      </div>
    </div>
  );
};

const UpcomingInvoiceSummaryModal: VoidComponent<{ upcomingInvoice: UpcomingInvoice }> = (props) => {
  const { t } = useTranslation();

  return (
    <DetailModal
      title={t(($) => $.UPCOMING_INVOICE_SUMMARY_MODAL_TITLE)}
      content={() => (
        <div class="flex flex-col gap-4 text-base-regular text-secondary">
          <div class="flex flex-col gap-1">
            <For each={props.upcomingInvoice.invoiceLines}>
              {(invoiceLine) => (
                <div class="flex justify-between">
                  <div>{invoiceLine.description}</div>
                  <div>
                    <FormatCurrency value={invoiceLine.amount / 100} currency={props.upcomingInvoice.currency} />
                  </div>
                </div>
              )}
            </For>
          </div>

          <SeparatorLine />

          <div class="flex flex-col gap-1">
            <Show when={props.upcomingInvoice.discount}>
              {(discount) => (
                <div class="flex justify-between">
                  <div>{t(($) => $.UPCOMING_INVOICE_SUMMARY_MODAL_COUPON)}</div>

                  <div class="text-brand">
                    <Switch>
                      <Match when={discount().coupon.type === "PERCENTAGE"}>{discount().coupon.amount + "%"}</Match>
                      <Match when={discount().coupon.type === "FIXED_AMOUNT"}>
                        <FormatCurrency
                          value={-(discount().coupon.amount / 100)}
                          currency={props.upcomingInvoice.currency}
                        />
                      </Match>
                    </Switch>
                  </div>
                </div>
              )}
            </Show>

            <div class="flex justify-between">
              <div>{t(($) => $.UPCOMING_INVOICE_SUMMARY_MODAL_SUBTOTAL)}</div>
              <FormatCurrency value={props.upcomingInvoice.subtotal / 100} currency={props.upcomingInvoice.currency} />
            </div>

            <div class="flex justify-between">
              <div>
                {t(($) => $.UPCOMING_INVOICE_SUMMARY_MODAL_VAT, { taxPercentage: props.upcomingInvoice.taxPercentage })}
              </div>
              <FormatCurrency value={props.upcomingInvoice.tax / 100} currency={props.upcomingInvoice.currency} />
            </div>

            <Show when={condition(props.upcomingInvoice.startingBalance, (balance) => !isNil(balance) && balance < 0)}>
              {(balance) => (
                <div class="flex justify-between">
                  <div>{t(($) => $.UPCOMING_INVOICE_SUMMARY_MODAL_CREDIT)}</div>
                  <div class="text-brand">
                    <FormatCurrency
                      value={Math.min(balance(), props.upcomingInvoice.amountDue) / 100}
                      currency={props.upcomingInvoice.currency}
                    />
                  </div>
                </div>
              )}
            </Show>
          </div>

          <SeparatorLine />

          <div class="flex justify-between text-header-lg text-primary">
            <div>{t(($) => $.UPCOMING_INVOICE_SUMMARY_MODAL_TOTAL)}</div>
            <FormatCurrency value={props.upcomingInvoice.amountDue / 100} currency={props.upcomingInvoice.currency} />
          </div>
        </div>
      )}
    />
  );
};

const InvoiceStatus: VoidComponent<{ status: Invoice["invoiceStatus"] }> = (props) => {
  const { t } = useTranslation();

  return (
    <Switch fallback={<Pill variant="danger" label={t(($) => $.SETTINGS_INVOICES_TABLE_STATUS_FAILED)} />}>
      <Match when={props.status === "OPEN"}>
        <Pill variant="neutral" label={t(($) => $.SETTINGS_INVOICES_TABLE_STATUS_OPEN)} />
      </Match>
      <Match when={props.status === "PAID"}>
        <Pill variant="success" label={t(($) => $.SETTINGS_INVOICES_TABLE_STATUS_PAID)} />
      </Match>
      <Match when={props.status === "PROCESSING"}>
        <Pill variant="neutral" label={t(($) => $.SETTINGS_INVOICES_TABLE_STATUS_PROCESSING)} />
      </Match>
    </Switch>
  );
};

const ProtectedInvoicesPage = withPagePermissionCheck(InvoicesPage, PermissionService.canManageBilling);

export default ProtectedInvoicesPage;
